import { useState, useEffect, useRef } from "react"

import {
  audioPlayer,
  Progress,
  PlaybackState,
  Track,
  Event,
} from "../lib/audio-player"
import { SkipMode, LoopMode } from "../lib/av/types"
import { useEventEmitterValue } from "./use-event-emitter-value"

export const usePlaybackState = (): PlaybackState => {
  const debounceTimeout = useRef<number>(undefined)
  const [playbackState, setPlaybackState] = useState(
    audioPlayer.getPlaybackState(),
  )
  useEffect(() => {
    const removeListener = audioPlayer.on(
      Event.PlaybackState,
      (nextPlaybackState) => {
        // Debounce all events *except* for "Loading" and "Buffering" for 250ms.
        // This prevents the play and pause buttons from flickering in the UI
        // every time there's a little hiccup in the audio player.
        window.clearTimeout(debounceTimeout.current)
        if (
          nextPlaybackState === PlaybackState.Loading ||
          nextPlaybackState === PlaybackState.Buffering
        ) {
          setPlaybackState(nextPlaybackState)
        } else {
          debounceTimeout.current = window.setTimeout(
            () => setPlaybackState(nextPlaybackState),
            250,
          )
        }
      },
    )
    return () => {
      window.clearTimeout(debounceTimeout.current)
      removeListener()
    }
  }, [])
  return playbackState
}

export const useTrack = (): Track | null =>
  useEventEmitterValue(audioPlayer, Event.Track, audioPlayer.getTrack)

export const useTracks = (): Track[] =>
  useEventEmitterValue(audioPlayer, Event.Tracks, audioPlayer.getTracks)

export const usePlaybackRate = (): number =>
  useEventEmitterValue(
    audioPlayer,
    Event.PlaybackRate,
    audioPlayer.getPlaybackRate,
  )

export const useSkipMode = (): SkipMode =>
  useEventEmitterValue(audioPlayer, Event.SkipMode, audioPlayer.getSkipMode)

export const useLoopMode = (): LoopMode =>
  useEventEmitterValue(audioPlayer, Event.LoopMode, audioPlayer.getLoopMode)

export const useProgress = (): Progress =>
  useEventEmitterValue(audioPlayer, Event.Progress, audioPlayer.getProgress)

export const useSeekable = (): boolean =>
  useEventEmitterValue(audioPlayer, Event.Seekable, audioPlayer.getSeekable)
